import { Box, Button, List, ListItem, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";

type StorageErrorProps = {
  reason: string;
};

/**
 * Displayed when the app cannot initialize its local IndexedDB-backed database.
 * Gives the user a clear next-step (clear storage and reload) instead of
 * crashing or rendering an empty page that silently no-ops every query.
 */
export default function StorageError({ reason }: StorageErrorProps) {
  const theme = useTheme();
  const [busy, setBusy] = useState(false);

  const handleClearStorage = async () => {
    if (!window.confirm("This will clear all local data and reload the app. Continue?")) {
      return;
    }
    setBusy(true);
    try {
      await clearAllIndexedDbDatabases();
      try {
        localStorage.clear();
      } catch (e) {
        console.warn("[StorageError] localStorage.clear failed", e);
      }
    } finally {
      window.location.reload();
    }
  };

  return (
    <Stack
      role="alert"
      sx={{
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        px: 3,
        py: 6,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          maxWidth: 560,
          width: "100%",
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
        }}
      >
        <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 700 }}>
          Local storage unavailable
        </Typography>

        <Typography variant="body1" sx={{ mb: 2 }}>
          We couldn&apos;t open the local database that Storyboard uses to keep things fast and offline-ready.
        </Typography>

        <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
          Reason: <code>{reason}</code>
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
          What you can try
        </Typography>
        <List dense sx={{ listStyleType: "disc", pl: 3, mb: 2 }}>
          <ListItem sx={{ display: "list-item", py: 0 }}>Free up disk space on your device</ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>Close other tabs running Storyboard</ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            Check that this site is allowed to store data in your browser settings
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            Clear cached site data, then reload (the button below does both)
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            If you&apos;re using the Storyboard desktop app, fully quit and reopen it
          </ListItem>
        </List>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button variant="contained" color="primary" onClick={handleClearStorage} disabled={busy}>
            {busy ? "Clearing…" : "Clear storage & reload"}
          </Button>
          <Button variant="outlined" color="primary" onClick={() => window.location.reload()} disabled={busy}>
            Reload
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}

async function clearAllIndexedDbDatabases(): Promise<void> {
  if (!window.indexedDB) return;

  // Older Safari and some Firefox versions don't expose `databases()`. We
  // can only do a best-effort cleanup there: reload after wiping localStorage.
  if (typeof indexedDB.databases !== "function") return;

  try {
    const databases = await indexedDB.databases();
    await Promise.all(
      databases.map(
        (db) =>
          new Promise<void>((resolve) => {
            if (!db.name) {
              resolve();
              return;
            }
            const request = indexedDB.deleteDatabase(db.name);
            request.onsuccess = () => resolve();
            request.onerror = () => resolve();
            request.onblocked = () => resolve();
          }),
      ),
    );
  } catch (e) {
    console.warn("[StorageError] Could not enumerate IndexedDB databases", e);
  }
}
